"use client"

import { useEffect, useState } from "react"
import { useSearchParams } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Search, ArrowLeft } from "lucide-react"
import DirectImage from "@/components/ui/direct-image"
import Link from "next/link"
import { getProducts, type ProductDisplay } from "@/lib/api/dutchie"
import ProductCard from "@/components/shop/product-card"
import { useCart } from "@/lib/context/cart-context"
import { createProductSlug } from "@/lib/slug-utils"
import { Pagination } from "@/components/shop/pagination"
import { usePagination } from "@/lib/hooks/use-pagination"
import { useListingScrollRestore } from "@/lib/hooks/use-listing-scroll"

export default function SearchPage() {
  const searchParams = useSearchParams()
  const searchQuery = searchParams.get("search") || ""
  const staffFilter = searchParams.get("staff") === "true"
  const saleFilter = searchParams.get("sale") === "true"
  const strainFilter = searchParams.get("strain") || ""
  const effectFilter = searchParams.get("effect") || ""
  const { addToCart } = useCart()
  
  const [products, setProducts] = useState<ProductDisplay[]>([])
  const [searchResults, setSearchResults] = useState<ProductDisplay[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentQuery, setCurrentQuery] = useState(searchQuery) // For the input field
  const [pageTitle, setPageTitle] = useState("Search Products")
  
  // Initialize pagination hook
  const pagination = usePagination({
    totalItems: searchResults.length,
    defaultItemsPerPage: 25
  })
  
  // Get paginated results
  const paginatedResults = pagination.paginate(searchResults)

  // Restore scroll on back-nav (page is already URL-driven via usePagination).
  useListingScrollRestore(!loading)

  useEffect(() => {
    async function fetchProducts() {
      try {
        setLoading(true)
        setError(null)
        console.log("Fetching products for search...")
        const allProducts = await getProducts()
        console.log(`Fetched ${allProducts.length} products for search`)

        if (allProducts.length === 0) {
          setError("No products found. You may need to run the update-cache script.")
        }
        
        setProducts(allProducts)
        
        // Determine which type of search/filter to apply
        if (staffFilter) {
          // Filter for staff picks
          const staffPicks = allProducts.filter(product => 
            product.collections.includes('staff-pick') && product.inStock
          )
          setSearchResults(staffPicks)
          setPageTitle("Staff Picks")
        } else if (saleFilter) {
          // Filter for sale items
          const saleItems = allProducts.filter(product => 
            product.collections.includes('on-sale') && product.inStock
          )
          setSearchResults(saleItems)
          setPageTitle("On Sale Items")
        } else if (strainFilter) {
          // Filter for specific strain type
          const strainItems = allProducts.filter(product => 
            product.strainType?.toLowerCase() === strainFilter.toLowerCase() && product.inStock
          )
          setSearchResults(strainItems)
          setPageTitle(`${strainFilter.charAt(0).toUpperCase() + strainFilter.slice(1)} Strains`)
        } else if (effectFilter) {
          // Filter for specific effect - map goal keywords to related effects/strains
          const effectKeywords: Record<string, string[]> = {
            'relax': ['relax', 'calm', 'sleep', 'sleepy', 'relaxed', 'peaceful', 'indica'],
            'pain': ['pain', 'relief', 'body', 'soothing', 'therapeutic'],
            'energy': ['energy', 'energetic', 'focus', 'creative', 'uplifting', 'sativa'],
            'social': ['happy', 'social', 'euphoric', 'giggly', 'talkative'],
          };
          const keywords = effectKeywords[effectFilter.toLowerCase()] || [effectFilter.toLowerCase()];
          
          const effectItems = allProducts.filter(product => {
            if (!product.inStock) return false;
            // Check effects
            const hasEffect = product.effects?.some(e => 
              keywords.some(k => e.toLowerCase().includes(k))
            );
            // Also check strain type for energy/relax
            const hasStrain = effectFilter === 'relax' 
              ? product.strainType?.toLowerCase() === 'indica'
              : effectFilter === 'energy'
                ? product.strainType?.toLowerCase() === 'sativa'
                : false;
            return hasEffect || hasStrain;
          });
          
          const titleMap: Record<string, string> = {
            'relax': 'Relax & Sleep',
            'pain': 'Pain & Relief',
            'energy': 'Energy & Focus',
            'social': 'Social & Mild',
          };
          setSearchResults(effectItems)
          setPageTitle(titleMap[effectFilter.toLowerCase()] || `${effectFilter.charAt(0).toUpperCase() + effectFilter.slice(1)} Products`)
        } else if (searchQuery) {
          // Perform text search if query exists
          performSearch(allProducts, searchQuery)
          setPageTitle(`Search Results for "${searchQuery}"`)
        } else {
          // No filters, show only in-stock products
          setSearchResults(allProducts.filter(p => p.inStock))
          setPageTitle("All Products")
        }
      } catch (err) {
        console.error("Error fetching products for search:", err)
        setError("Failed to load products. Please try again later.")
        setProducts([])
        setSearchResults([])
      } finally {
        setLoading(false)
      }
    }

    fetchProducts()
  }, [searchQuery, staffFilter, saleFilter, strainFilter, effectFilter])
  
  // Function to perform search on products
  const performSearch = (productList: ProductDisplay[], query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }
    
    const normalizedQuery = query.toLowerCase().trim()
    const queryTerms = normalizedQuery.split(/\s+/)
    
    const results = productList.filter(product => {
      // Check if any query term matches product name, brand, category, strain type, or effects
      return queryTerms.some(term => {
        return (
          product.name.toLowerCase().includes(term) ||
          product.brand.toLowerCase().includes(term) ||
          product.category.toLowerCase().includes(term) ||
          (product.strainType && product.strainType.toLowerCase().includes(term)) ||
          (product.effects && product.effects.some(effect => effect.toLowerCase().includes(term)))
        )
      })
    })
    
    setSearchResults(results)
  }
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Update URL with search query without full page reload
    const url = new URL(window.location.href)
    url.searchParams.set("search", currentQuery)
    window.history.pushState({}, "", url.toString())
    
    // Perform search
    performSearch(products, currentQuery)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="container mx-auto px-4">
        {/* Back button and search form */}
        <div className="mb-8 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div>
            <Link href="/shop" className="inline-flex items-center text-gray-600 hover:text-primary_green mb-2">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Shop
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">
              {pageTitle}
            </h1>
          </div>
          
          <form onSubmit={handleSearch} className="w-full md:w-auto">
            <div className="flex w-full md:w-96 rounded-lg overflow-hidden border border-gray-300 bg-white shadow-sm">
              <Input
                type="text"
                value={currentQuery}
                onChange={(e) => setCurrentQuery(e.target.value)}
                placeholder="Search products, strains, effects..."
                className="border-0 focus-visible:ring-0 flex-grow"
              />
              <Button type="submit" className="rounded-none bg-primary_green hover:bg-primary_green/90">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
          </form>
        </div>

        {/* Search results */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary_green mx-auto mb-4"></div>
              <p className="text-lg text-gray-600">Searching our cannabis collection...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12 bg-red-50 rounded-xl border border-red-100 p-8">
              <p className="text-red-500 font-medium">{error}</p>
              <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>Try Again</Button>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="text-center py-12">
              <DirectImage 
                src="/images/placeholder.png" 
                alt="No results found" 
                width={200} 
                height={200} 
                className="mx-auto mb-4 opacity-50"
                imgClassName="mx-auto mb-4 opacity-50"
                fallbackSrc="/placeholder-image.png"
              />
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Results Found</h2>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                {staffFilter ? 
                  "We don't have any staff picks available right now. Please check back soon." : 
                 saleFilter ? 
                  "We don't have any items on sale right now. Please check back soon." : 
                 strainFilter ? 
                  `We couldn't find any products with the strain type "${strainFilter}". Try browsing all products.` : 
                 effectFilter ? 
                  `We couldn't find any products with the effect "${effectFilter}". Try browsing all products.` : 
                 searchQuery ? 
                  `We couldn't find any products matching "${searchQuery}". Try using different keywords or browse our categories.` : 
                  "No products found in this category. Try browsing all products."}
              </p>
              <Link href="/shop">
                <Button className="bg-primary_green hover:bg-primary_green/90">Browse All Products</Button>
              </Link>
            </div>
          ) : !searchQuery && !staffFilter && !saleFilter && !strainFilter && !effectFilter ? (
            <div className="text-center py-12">
              <h2 className="text-xl font-medium text-gray-700 mb-4">Enter a search term to find products</h2>
              <p className="text-gray-500">You can search by product name, strain type, effects, or category</p>
            </div>
          ) : (
            <div>
              <div className="mb-4 flex justify-between items-center">
                <p className="text-gray-600">{searchResults.length} {searchResults.length === 1 ? 'product' : 'products'} found</p>
                <div className="text-sm text-gray-500">
                  Showing {pagination.currentPage === 1 ? '1' : ((pagination.currentPage - 1) * pagination.itemsPerPage + 1)} 
                  - {Math.min(pagination.currentPage * pagination.itemsPerPage, searchResults.length)} 
                  of {searchResults.length}
                </div>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
                {paginatedResults.map((product) => {
                  // Create a URL parameters object to store current filter state
                  const filterParams = new URLSearchParams();
                  
                  // Add all the current search parameters to preserve filter state
                  if (searchQuery) filterParams.append('search', searchQuery);
                  if (staffFilter) filterParams.append('staff', 'true');
                  if (saleFilter) filterParams.append('sale', 'true');
                  if (strainFilter) filterParams.append('strain', strainFilter);
                  if (effectFilter) filterParams.append('effect', effectFilter);
                  
                  // Create the current page URL with all filters
                  const currentPageWithFilters = `/shop/search?${filterParams.toString()}`;
                  
                  // Product detail URL with slug, variant and source page reference
                  const productDetailUrl = `/shop/product/${createProductSlug(product)}?from=${encodeURIComponent(currentPageWithFilters)}${product.variantId ? `&variant=${product.variantId}` : ''}`;
                  
                  return (
                    <Link 
                      key={product.id} 
                      href={productDetailUrl}
                      onClick={() => {
                        // Store referrer information for back navigation
                        if (typeof window !== 'undefined') {
                          sessionStorage.setItem('productReferrer', currentPageWithFilters);
                          const state = { ...window.history.state, previousPage: currentPageWithFilters };
                          window.history.replaceState(state, '');
                        }
                      }}
                      className="block h-full"
                    >
                      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow h-full flex flex-col group">
                        <div className="relative aspect-square bg-white">
                          <DirectImage
                          src={product.image || "/placeholder.png"}
                          alt={product.name}
                          fill
                          className="object-contain p-4"
                          imgClassName="object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                          fallbackSrc="/placeholder-image.png"
                        />
                        {product.strainType && (
                          <div className={`absolute top-2 left-2 text-xs font-bold px-3 py-1 rounded-full flex items-center
                            ${product.strainType === "sativa" ? "bg-yellow-100 text-yellow-800" :
                              product.strainType === "indica" ? "bg-purple-100 text-purple-800" :
                              "bg-green-100 text-green-800"}`
                          }>
                            {product.strainType.charAt(0).toUpperCase() + product.strainType.slice(1)}
                          </div>
                        )}
                      </div>
                      <div className="p-4 flex-grow">
                        <p className="text-xs font-medium text-gray-500">{product.brand}</p>
                        <h3 className="font-medium text-gray-900 line-clamp-2 mt-1 mb-1 group-hover:text-primary_green transition-colors">{product.name}</h3>
                        
                        <div className="mt-3 flex justify-between items-center">
                          <div>
                            {product.specialPrice ? (
                              <div className="flex flex-col">
                                <span className="text-red-600 font-semibold">${product.specialPrice.toFixed(2)}</span>
                                <span className="text-gray-500 text-xs line-through">${product.price.toFixed(2)}</span>
                              </div>
                            ) : (
                              <span className="font-medium">${product.price.toFixed(2)}</span>
                            )}
                          </div>
                          <Button 
                            size="sm" 
                            className="h-9 w-9 rounded-full p-0 bg-primary_green hover:bg-primary_green/90 shadow-sm"
                            onClick={(e) => {
                              e.preventDefault();
                              addToCart(product, 1);
                            }}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
              </div>
              {/* Pagination controls */}
              {searchResults.length > 0 && (
                <Pagination
                  totalItems={searchResults.length}
                  currentPage={pagination.currentPage}
                  itemsPerPage={pagination.itemsPerPage}
                  onPageChange={pagination.goToPage}
                  onItemsPerPageChange={pagination.changeItemsPerPage}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
